/** @fileoverview Loads and renders saved and streaming assistant messages. */

import { Message, MessageContent } from '../ai-elements/message';
import { Button } from '../ui/button';
import LoadingState from '../ui/loading-state';
import ChatMessage from './ChatMessage';
import LiveStatus from './LiveStatus';
import type { MessageListProps } from './types';

type Props = MessageListProps & {
  sendingText?: string;
  hideActivityMessageId?: string;
};

export default function MessageList({
  messages,
  savedMessages,
  busy,
  loading,
  hasMoreMessages,
  status,
  onRate,
  onLoadMore,
  sendingText,
  hideActivityMessageId,
}: Props) {
  const last = messages[messages.length - 1];
  const lastAssistant = last?.role === 'assistant' ? last : undefined;
  // The streaming assistant message renders its own activity trigger, even before content arrives.
  const waitingForAssistant = !sendingText && busy && !lastAssistant;
  const savedMessagesById = new Map(savedMessages.map((message) => [message.id, message]));

  return (
    <div className="flex w-full min-w-0! max-w-full flex-col gap-3">
      {hasMoreMessages &&
        messages.length > 0 &&
        (loading ? (
          <LoadingState className="py-1">Loading earlier messages…</LoadingState>
        ) : (
          <Button
            type="button"
            variant="ghost"
            className="mx-auto h-auto rounded-full px-3 py-1.5 text-xs text-app-text-muted"
            disabled={busy}
            onClick={onLoadMore}
          >
            Load earlier messages
          </Button>
        ))}
      {loading && !messages.length && <LoadingState>Loading conversation…</LoadingState>}
      {messages.map((message) => (
        <ChatMessage
          message={message}
          savedMessage={savedMessagesById.get(message.id)}
          busy={busy}
          lastMessageId={last?.id}
          onRate={onRate}
          hideActivity={message.id === hideActivityMessageId}
          key={message.id}
        />
      ))}
      {sendingText && (
        <Message className="ml-auto w-fit min-w-0 max-w-4xl gap-1" from="user">
          <div className="w-full text-right text-xs text-app-text-muted">Sending</div>
          <MessageContent className="min-w-0 wrap-anywhere">{sendingText}</MessageContent>
        </Message>
      )}
      {waitingForAssistant && (
        <Message from="assistant">
          <MessageContent className="rounded-none bg-transparent px-0 py-0 text-app-text-muted">
            {/* Keyed on the last message so the clock starts at zero for every new turn. */}
            <LiveStatus entries={[]} waitingForAnswer={false} key={last?.id} />
          </MessageContent>
        </Message>
      )}
      {status === 'error' && (
        <div role="status" className="sr-only">
          Reply failed.
        </div>
      )}
    </div>
  );
}
